import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createCostCode, updateCostCode } from '../../store/slices/settingsSlice';
import Dialog from '../common/Dialog';
import LoadingSpinner from '../common/LoadingSpinner';

const AddEditCostCodeDialog = ({ isOpen, mode, data, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { user: currentUser } = useSelector(state => state.auth);
  const userRole = currentUser?.profile?.role;
  const hasEditPermission = ['super_admin', 'admin'].includes(userRole);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    item: '',
    description: '',
    unit: '',
    unitCost: '',
    builderCost: '',
    clientPrice: '',
    margin: ''
  });

  const inputClasses = `
    w-full border border-gray-200 px-4 py-2.5 rounded-md
    focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gray-900
    bg-white transition-colors
  `;

  const textAreaClasses = `
    w-full border border-gray-200 px-4 py-2.5 rounded-md
    focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gray-900
    bg-white resize-none transition-colors
  `;

  const labelClasses = "block text-sm font-medium text-gray-700 mb-1";

  useEffect(() => {
    if (mode === 'edit' && data) {
      setFormData({
        item: data.item,
        description: data.description,
        unit: data.unit,
        unitCost: data.unitCost,
        builderCost: data.builderCost,
        clientPrice: data.clientPrice,
        margin: data.margin
      });
    } else {
      setFormData({
        item: '',
        description: '',
        unit: '',
        unitCost: '',
        builderCost: '',
        clientPrice: '',
        margin: ''
      });
    }
  }, [mode, data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateMargin = () => {
    const builderCost = parseFloat(formData.builderCost) || 0;
    const clientPrice = parseFloat(formData.clientPrice) || 0;
    if (builderCost > 0 && clientPrice > 0) {
      const margin = ((clientPrice - builderCost) / builderCost * 100).toFixed(2);
      setFormData(prev => ({ ...prev, margin }));
    }
  };

  useEffect(() => {
    calculateMargin();
  }, [formData.builderCost, formData.clientPrice]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'edit') {
        await dispatch(updateCostCode({ id: data._id, updates: formData })).unwrap();
      } else {
        await dispatch(createCostCode(formData)).unwrap();
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save cost code:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">
            {hasEditPermission ? (mode === 'edit' ? 'Edit Cost Code' : 'Add Cost Code') : 'View Cost Code'}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className={labelClasses}>Item</label>
            <input
              type="text"
              name="item"
              value={formData.item}
              onChange={handleChange}
              required
              readOnly={!hasEditPermission}
              className={`${inputClasses} ${!hasEditPermission ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
          </div>

          <div>
            <label className={labelClasses}>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              readOnly={!hasEditPermission}
              className={`${textAreaClasses} ${!hasEditPermission ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>Unit</label>
              <input
                type="text"
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                required
                readOnly={!hasEditPermission}
                className={`${inputClasses} ${!hasEditPermission ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
            </div>
            <div>
              <label className={labelClasses}>Unit Cost</label>
              <input
                type="number"
                name="unitCost"
                value={formData.unitCost}
                onChange={handleChange}
                required
                step="0.01"
                min="0"
                readOnly={!hasEditPermission}
                className={`${inputClasses} ${!hasEditPermission ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>Builder Cost</label>
              <input
                type="number"
                name="builderCost"
                value={formData.builderCost}
                onChange={handleChange}
                required
                step="0.01"
                min="0"
                readOnly={!hasEditPermission}
                className={`${inputClasses} ${!hasEditPermission ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
            </div>
            <div>
              <label className={labelClasses}>Client Price</label>
              <input
                type="number"
                name="clientPrice"
                value={formData.clientPrice}
                onChange={handleChange}
                required
                step="0.01"
                min="0"
                readOnly={!hasEditPermission}
                className={`${inputClasses} ${!hasEditPermission ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
            </div>
          </div>

          <div>
            <label className={labelClasses}>Margin (%)</label>
            <input
              type="number"
              name="margin"
              value={formData.margin}
              readOnly
              className={`${inputClasses} bg-gray-50`}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {hasEditPermission ? 'Cancel' : 'Close'}
            </button>
            {hasEditPermission && (
              <button
                type="submit"
                disabled={loading}
                className="relative px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                {loading ? (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <LoadingSpinner size="small" />
                  </span>
                ) : mode === 'edit' ? 'Save Changes' : 'Create'}
              </button>
            )}
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default AddEditCostCodeDialog; 